import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useInfiniteQuery, useQuery } from "@tanstack/vue-query";
import axios from "axios";
import {
  CategoryType,
  SearchApi,
  type SearchApiV2SearchAllGetRequest,
  type SearchApiV2SearchCategoriesGetRequest,
  type SearchApiV2SearchEventsGetRequest,
  type SearchApiV2SearchGetRequest,
  type SearchApiV2SearchProductsGetRequest,
} from "mws-ts-v2-sdk";

import type { QuickSearchResults } from "~/types/Search";

let searchApi: SearchApi;
const getSearchApi = () => {
  // reduce memory overhead by only using one instance
  if (!searchApi) {
    searchApi = new SearchApi(
      undefined,
      useRuntimeConfig().public.catalogServiceUrl,
      axios,
    );
  }

  return searchApi;
};

export const searchQueryKeys = createQueryKeys("search", {
  // Old search endpoint still used for the modal
  results: (params: MaybeRef<SearchApiV2SearchGetRequest>) => ({
    queryKey: [params],
    queryFn: async () => {
      const response = await getSearchApi().v2SearchGet(unref(params));

      return response.data;
    },
  }),
  all: (params: MaybeRef<SearchApiV2SearchAllGetRequest>) => ({
    queryKey: [params],
    queryFn: async () => {
      const response = await getSearchApi().v2SearchAllGet(unref(params));

      return response.data;
    },
  }),
  quickSearch: (
    platformId: MaybeRef<string>,
    searchTerm: MaybeRef<string>,
    categoryTypes: Ref<Array<CategoryType>> | Ref<undefined>,
  ) => ({
    queryKey: [searchTerm],
    queryFn: async () => {
      const unreffedSearchTerm = unref(searchTerm);

      const [products, suggestions] = await Promise.all([
        getSearchApi().v2SearchProductsGet({
          searchTerm: unreffedSearchTerm,
          xPlatformKey: unref(platformId),
          pageSize: 4,
        }),
        getSearchApi().v2SearchSuggestionsGet({
          xPlatformKey: unref(platformId),
          searchTerm: unreffedSearchTerm,
          numberOfSuggestions: 5,
          categoryTypes: categoryTypes?.value,
        }),
      ]);

      return {
        products: products.data.results,
        suggestions: suggestions.data,
        searchTerm: unreffedSearchTerm,
        hasResults: products.data.results.length + suggestions.data.length > 0,
      } satisfies QuickSearchResults;
    },
  }),
});

export const useInfiniteCategoriesSearchQuery = (
  params: MaybeRef<SearchApiV2SearchCategoriesGetRequest>,
  enabled: Ref<boolean>,
) => {
  return useInfiniteQuery({
    queryKey: ["search", "categories", "infiniteList", params],
    queryFn: async ({ pageParam }) => {
      const response = await getSearchApi().v2SearchCategoriesGet({
        ...unref(params),
        offset: pageParam,
      });

      return response.data;
    },
    enabled,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextPageOffset = lastPage.offset + (unref(params).pageSize || 25);
      if (nextPageOffset >= lastPage.totalCount) return undefined; // no next page

      return nextPageOffset;
    },
  });
};

export const useInfiniteProductsSearchQuery = (
  params: MaybeRef<SearchApiV2SearchProductsGetRequest>,
  enabled: Ref<boolean>,
) => {
  return useInfiniteQuery({
    queryKey: ["search", "products", "infiniteList", params],
    queryFn: async ({ pageParam }) => {
      const response = await getSearchApi().v2SearchProductsGet({
        ...unref(params),
        pageSize: unref(params).pageSize || 20,
        offset: pageParam,
      });

      return response.data;
    },
    enabled,
    placeholderData: (data) => data,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextPageOffset = lastPage.offset + (unref(params).pageSize || 20);
      if (nextPageOffset >= lastPage.totalCount) return undefined; // no next page

      return nextPageOffset;
    },
  });
};

export const useInfiniteEventsSearchQuery = (
  params: MaybeRef<SearchApiV2SearchEventsGetRequest>,
  enabled: Ref<boolean>,
) => {
  return useInfiniteQuery({
    queryKey: ["search", "events", "infiniteList", params],
    queryFn: async ({ pageParam }) => {
      const response = await getSearchApi().v2SearchEventsGet({
        ...unref(params),
        offset: pageParam,
      });

      return response.data;
    },
    enabled,
    placeholderData: (data) => data,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextPageOffset = lastPage.offset + (unref(params).pageSize || 25);
      if (nextPageOffset >= lastPage.totalCount) return undefined; // no next page

      return nextPageOffset;
    },
  });
};

export const useQuickSearchQuery = (
  searchTerm: Ref<string>,
  options?: {
    enabled: Ref<boolean>;
  },
) => {
  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );
  const isAuctionsEnabled =
    platformStore.getPlatformConfiguration.isAuctionsEnabled;

  const categoryTypes = computed(() =>
    isAuctionsEnabled ? undefined : [CategoryType.Athlete, CategoryType.Team],
  );

  const previousSearchTerm = usePrevious(searchTerm);

  return useQuery({
    ...searchQueryKeys.quickSearch(platformId, searchTerm, categoryTypes),
    enabled: options?.enabled,
    placeholderData: (previousData) => {
      if (
        previousData?.searchTerm === previousSearchTerm.value &&
        searchTerm.value
      ) {
        return previousData;
      }
    },
  });
};
